// ink-component
import React, { ReactNode, MouseEvent, useContext, RefObject } from 'react';
import styled from 'styled-components';
import Anchor, { LinkPresetsType } from '../../../Anchor/Anchor';
import useInkAnalytics from '../../../../hooks/useInkAnalytics';
import DropdownContext from '../../contexts/DropdownContext';

export interface DropdownAnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** HTML attribute */
  href?: string;
  /** Anchor's text */
  children?: ReactNode;
  /** onClick callback */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** preset attribute */
  preset?: LinkPresetsType;
  /** target attribute */
  target?: string;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLAnchorElement>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  render?: () => void;
}

const StyledLabel = styled.label`
  /* Using !important here to override the adopted styles via 'className' */
  padding: 0 !important;
`;

/**
 * @purpose An anchor for Dropdown.Item(s)
 * @group Dropdown
 * @categories navigation
 * @HTMLType HTMLAnchorElement
 */
function DropdownAnchor(props: DropdownAnchorProps) {
  const { close, preventClose } = useContext(DropdownContext);
  const { render, externalRef, className, id, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });

  const handleClick = args => {
    if (props.onClick) {
      props.onClick(args);
    }
    if (!preventClose) {
      close();
    }
  };

  return (
    /* we're disabling this rule because the presence of role="button"
    /* ensures the element can be pre pressed with the "enter" key */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events

    <StyledLabel className={className} tabIndex={-1} htmlFor={id}>
      <Anchor
        {...otherProps}
        externalRef={ref}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        type={undefined}
        isDropdown
        isDisabled={props.disabled}
      >
        {props.children}
      </Anchor>
    </StyledLabel>
  );
}

DropdownAnchor.defaultProps = {
  children: undefined,
  onClick: undefined,
  target: undefined,
  href: undefined,
  'data-track-events': ['click'],
};

DropdownAnchor.displayName = 'Dropdown.Anchor';

export default DropdownAnchor;
